/**
 * Voucher Enabler Plugin
 * Automatically applies discount code on first visit
 * and updates displayed prices on the website
 *
 * IMPORTANT: Prices are only updated when the discount is actually
 * verified to be in the cart/checkout session.
 */

(function() {
  // Get config from page (set by Liquid)
  const CONFIG = window.VOUCHER_ENABLER_CONFIG || {};

  const DISCOUNT_CODE = CONFIG.discountCode || 'NEUJAHR16';
  const DISCOUNT_PERCENT = CONFIG.discountPercent || 16;
  const MAX_PRICE_CENTS = CONFIG.maxPriceCents || 25000; // 250 EUR in cents
  const APPLY_TO_SUBSCRIPTIONS = CONFIG.applyToSubscriptions === true;
  const APPLY_TO_GIFTCARDS = CONFIG.applyToGiftcards === true;

  // Cookie name is keyed by discount code so changing the voucher in theme settings
  // automatically applies the new code for all users (no old cookie = first visit for new code)
  const COOKIE_NAME = 'voucher_' + (DISCOUNT_CODE || 'state').toLowerCase().replace(/[^a-z0-9]/g, '_') + '_state';
  const COOKIE_DAYS = 30; // Cookie expires in 30 days

  // Track if discount is verified in cart
  let discountVerified = false;

  /**
   * Get cookie value
   */
  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  /**
   * Set cookie value
   */
  function setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
  }

  /**
   * Check if user has removed the voucher (opted out)
   */
  function hasUserRemovedVoucher() {
    return getCookie(COOKIE_NAME) === 'removed';
  }

  /**
   * Mark voucher as applied
   */
  function markVoucherApplied() {
    setCookie(COOKIE_NAME, 'applied', COOKIE_DAYS);
  }

  /**
   * Mark voucher as removed by user (opt out)
   */
  function markVoucherRemoved() {
    setCookie(COOKIE_NAME, 'removed', COOKIE_DAYS);
    discountVerified = false;
  }

  /**
   * Check if voucher is actually in the cart (verified)
   */
  function isVoucherActive() {
    return discountVerified;
  }

  /**
   * Parse a German price string to cents
   * e.g., "49,99 €" -> 4999, "Angebot 49,99 €" -> 4999, "ab 1.234,56 €" -> 123456
   */
  function parsePriceToCents(priceString) {
    if (!priceString) return 0;

    // Extract all number sequences with comma/dot (e.g., "49,99" or "1.234,56")
    const matches = priceString.match(/[\d]+[.,\d]*/g);
    if (!matches || matches.length === 0) return 0;

    // Get the last match (the actual price, handles cases like "ab 49,99")
    let cleaned = matches[matches.length - 1];

    // Handle German number format (1.234,56)
    // Check if it has a comma (German decimal separator)
    if (cleaned.includes(',')) {
      // Remove thousand separators (dots) and replace decimal comma with dot
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    }

    const price = parseFloat(cleaned);
    if (isNaN(price)) return 0;

    return Math.round(price * 100);
  }

  /**
   * Format cents to German price string
   * e.g., 4999 -> "49,99 €"
   */
  function formatCentsToPrice(cents) {
    const euros = (cents / 100).toFixed(2);
    // Convert to German format
    const germanFormat = euros.replace('.', ',');
    return germanFormat + ' €';
  }

  /**
   * Calculate discounted price
   */
  function calculateDiscountedPrice(originalCents) {
    const discount = Math.round(originalCents * (DISCOUNT_PERCENT / 100));
    return originalCents - discount;
  }

  /**
   * Verify discount is actually applied by checking server-rendered cart drawer
   * This is the ONLY reliable way to know if Shopify has the discount in session
   */
  async function verifyDiscountInCart() {
    try {
      // Fetch fresh cart drawer content from the server using Section Rendering API
      const response = await fetch('/?sections=cart-drawer', {
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        console.log('VoucherEnabler: Failed to fetch cart drawer section');
        return false;
      }

      const data = await response.json();
      const cartDrawerHtml = data['cart-drawer'] || '';

      // Check if the HTML contains indicators that a discount is applied
      // The cart drawer shows "remove_discount" link ONLY when cart.discount_applications.size > 0
      const discountCodeFound = cartDrawerHtml.toUpperCase().includes(DISCOUNT_CODE.toUpperCase());
      const hasRemoveDiscountLink = cartDrawerHtml.includes('id="remove_discount"');

      // If remove_discount link exists, a discount IS applied (from cart-drawer.liquid logic)
      const isApplied = discountCodeFound || hasRemoveDiscountLink;

      console.log('VoucherEnabler: Verify discount result:', {
        discountCodeFound,
        hasRemoveDiscountLink,
        isApplied
      });

      return isApplied;
    } catch (error) {
      console.error('VoucherEnabler: Error verifying discount:', error);
      return false;
    }
  }

  /**
   * Check discount in cart using DOM (for real-time checks when cart drawer is open)
   */
  function checkDiscountInDom() {
    // Check for discount badge in the cart drawer
    const discountBadge = document.querySelector('.discount-badge');
    if (discountBadge) {
      const badgeText = discountBadge.textContent.toUpperCase();
      if (badgeText.includes(DISCOUNT_CODE.toUpperCase())) {
        return true;
      }
    }

    // Check for "Rabatt entfernen" link (only shown when discount is applied)
    const removeDiscountLink = document.getElementById('remove_discount');
    if (removeDiscountLink) {
      return true;
    }

    return false;
  }

  /**
   * Apply the discount code via Shopify endpoint
   */
  async function applyDiscount() {
    const discountUrl = `${window.location.origin}/discount/${DISCOUNT_CODE}`;
    console.log('VoucherEnabler: Applying discount via', discountUrl);

    try {
      const response = await fetch(discountUrl, {
        method: 'GET',
        redirect: 'follow',
        credentials: 'same-origin'
      });

      console.log('VoucherEnabler: Response', {
        ok: response.ok,
        redirected: response.redirected,
        status: response.status,
        url: response.url
      });

      if (response.redirected || response.ok) {
        return true;
      }
      return false;
    } catch (error) {
      console.error('Voucher Enabler: Error applying discount', error);
      return false;
    }
  }

  /**
   * Check if a price element belongs to a subscription product
   */
  function isSubscriptionProduct(element) {
    // Check for line-item with selling plan (cart)
    const lineItem = element.closest('line-item[data-selling-plan]');
    if (lineItem) return true;

    // Check for subscription indicators in parent elements
    const parent = element.closest('[data-selling-plan], [data-subscription], .subscription-product');
    if (parent) return true;

    // Check if this specific price is inside a subscription variant/option
    const priceContainer = element.closest('.variant-picker__option, .product-card');
    if (priceContainer) {
      // Only check immediate subscription-related labels, not entire page content
      const subscriptionLabels = priceContainer.querySelectorAll('.subscription-label, [data-subscription-option], .selling-plan-label');
      if (subscriptionLabels.length > 0) return true;

      // Check for "Abo" in variant option labels only (not entire content)
      const optionLabels = priceContainer.querySelectorAll('.block-swatch__label, .option-value__label, label');
      for (const label of optionLabels) {
        const labelText = label.textContent.toLowerCase();
        if (labelText.includes('abo') && labelText.length < 50) { // Short label, likely an option
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Check if a price element belongs to a gift card product
   */
  function isGiftcardProduct(element) {
    // Check for gift card indicators in parent elements
    const parent = element.closest('[data-gift-card], .gift-card, .giftcard');
    if (parent) return true;

    // Check if element is inside a link to a gift card product
    const link = element.closest('a[href*="gutschein"], a[href*="gift-card"], a[href*="giftcard"]');
    if (link) return true;

    // Check current page URL for gift card products
    if (window.location.pathname.includes('gutschein') ||
        window.location.pathname.includes('gift-card') ||
        window.location.pathname.includes('giftcard')) {
      return true;
    }

    // Check any links within the product card container
    const container = element.closest('product-card, .product-card, article, [class*="featured-product"]');
    if (container) {
      const productLink = container.querySelector('a[href*="gutschein"], a[href*="gift-card"], a[href*="giftcard"]');
      if (productLink) return true;

      // Check product title
      const title = container.querySelector('.product-card__title, .product-title, h2, h3');
      if (title) {
        const titleText = title.textContent.toLowerCase();
        if (titleText.includes('gutschein') || titleText.includes('gift card')) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Update prices in Recharge subscription widget (Shadow DOM)
   */
  function updateRechargeWidgetPrices() {
    // Find all Recharge subscription widgets
    const rechargeWidgets = document.querySelectorAll('recharge-subscription-widget');

    rechargeWidgets.forEach(widget => {
      const shadowRoot = widget.shadowRoot;
      if (!shadowRoot) return;

      // Find the one-time purchase option price (not the subscription one)
      const onetimeOption = shadowRoot.querySelector('.rc-selection__onetime, .rc-purchase-option__onetime');
      if (!onetimeOption) return;

      // Find the price element inside the one-time option
      const priceEl = onetimeOption.querySelector('.rc-price:not(.strike-through)');
      if (!priceEl || priceEl.dataset.voucherProcessed) return;

      const priceText = priceEl.textContent.trim();
      const priceCents = parsePriceToCents(priceText);

      // Skip if no valid price or above max
      if (priceCents <= 0 || priceCents >= MAX_PRICE_CENTS) return;

      // Store original price
      priceEl.dataset.voucherProcessed = 'true';
      priceEl.dataset.voucherOriginalPrice = priceText;

      // Calculate and display discounted price
      const discountedCents = calculateDiscountedPrice(priceCents);
      const discountedPrice = formatCentsToPrice(discountedCents);

      priceEl.textContent = discountedPrice;
      priceEl.style.color = '#307a07';
      priceEl.style.fontWeight = '600';

      console.log('VoucherEnabler: Updated Recharge widget price from', priceText, 'to', discountedPrice);
    });
  }

  /**
   * Revert prices in Recharge subscription widget (Shadow DOM)
   */
  function revertRechargeWidgetPrices() {
    // Revert in regular DOM
    const processedElements = document.querySelectorAll('[data-voucher-processed="true"]');
    processedElements.forEach(el => {
      if (el.dataset.voucherOriginalPrice) {
        el.textContent = el.dataset.voucherOriginalPrice;
        el.style.color = '';
        el.style.fontWeight = '';
      }
      delete el.dataset.voucherProcessed;
      delete el.dataset.voucherOriginalPrice;
    });

    // Revert in Recharge Shadow DOM
    const rechargeWidgets = document.querySelectorAll('recharge-subscription-widget');
    rechargeWidgets.forEach(widget => {
      const shadowRoot = widget.shadowRoot;
      if (!shadowRoot) return;

      const processedInShadow = shadowRoot.querySelectorAll('[data-voucher-processed="true"]');
      processedInShadow.forEach(el => {
        if (el.dataset.voucherOriginalPrice) {
          el.textContent = el.dataset.voucherOriginalPrice;
          el.style.color = '';
          el.style.fontWeight = '';
          console.log('VoucherEnabler: Reverted Recharge widget price to', el.dataset.voucherOriginalPrice);
        }
        delete el.dataset.voucherProcessed;
        delete el.dataset.voucherOriginalPrice;
      });
    });
  }

  /**
   * Update all prices on the page to show discounted prices
   * Only applies when voucher is active
   */
  function updatePricesOnPage() {
    // Only update prices if voucher is active
    if (!isVoucherActive()) return;

    // Update Recharge widget prices
    updateRechargeWidgetPrices();

    // Find all price-list elements
    const priceLists = document.querySelectorAll('price-list');

    priceLists.forEach(priceList => {
      // Skip if already processed
      if (priceList.dataset.voucherEnablerProcessed) return;

      // Skip cart - Shopify already handles discounted prices there
      if (priceList.closest('cart-drawer, .cart, #cart, [data-cart], cart-items, line-item')) {
        priceList.dataset.voucherEnablerProcessed = 'true';
        return;
      }

      // Skip subscription products if setting is disabled
      if (!APPLY_TO_SUBSCRIPTIONS && isSubscriptionProduct(priceList)) {
        priceList.dataset.voucherEnablerProcessed = 'true';
        return;
      }

      // Skip gift cards if setting is disabled
      if (!APPLY_TO_GIFTCARDS && isGiftcardProduct(priceList)) {
        priceList.dataset.voucherEnablerProcessed = 'true';
        return;
      }

      const salePrice = priceList.querySelector('sale-price');
      if (!salePrice) return;

      // Get the price text - parsePriceToCents will extract just the number
      const priceText = salePrice.textContent;
      const priceCents = parsePriceToCents(priceText);

      // Only apply to products under max price
      if (priceCents <= 0 || priceCents >= MAX_PRICE_CENTS) return;

      // Get current font size to use for scaling
      const computedStyle = window.getComputedStyle(salePrice);
      const currentFontSize = parseFloat(computedStyle.fontSize);
      const smallerSize = Math.max(currentFontSize * 0.7, 12); // 70% of current size, min 12px

      // Check if there's already a compare-at-price showing a higher price (already on sale)
      const existingCompare = priceList.querySelector('compare-at-price');
      if (existingCompare) {
        const compareCents = parsePriceToCents(existingCompare.textContent);
        // If already on sale with a higher compare price, just reorder and style for consistency
        if (compareCents > priceCents) {
          // Move compare-at-price BEFORE sale-price and apply consistent styling
          existingCompare.style.cssText = `text-decoration: line-through; color: #e32c2b; font-size: ${smallerSize}px; margin-right: 0.5em; font-weight: 400;`;
          existingCompare.className = '';
          salePrice.before(existingCompare);

          // Style the sale price consistently (green)
          salePrice.classList.remove('text-subdued', 'text-on-sale');
          salePrice.style.color = '#307a07';
          salePrice.style.fontWeight = '600';

          priceList.dataset.voucherEnablerProcessed = 'true';
          return;
        }
      }

      // Store original data for reverting later (store full innerHTML)
      priceList.dataset.voucherOriginalHtml = salePrice.innerHTML;
      priceList.dataset.voucherOriginalStyle = salePrice.getAttribute('style') || '';
      priceList.dataset.voucherOriginalClasses = salePrice.className;

      // Calculate discounted price
      const discountedCents = calculateDiscountedPrice(priceCents);
      const discountedPrice = formatCentsToPrice(discountedCents);
      const originalPrice = formatCentsToPrice(priceCents);

      // Store original content
      const srOnly = salePrice.querySelector('.sr-only');
      const srOnlyHtml = srOnly ? srOnly.outerHTML : '';

      // Remove any existing compare-at-price to ensure consistent ordering
      if (existingCompare) {
        existingCompare.remove();
      }

      // Create new compare-at-price BEFORE sale-price (OLD original price - RED, smaller, crossed out)
      const compareAtPrice = document.createElement('compare-at-price');
      compareAtPrice.classList.add('voucher-compare-price');
      compareAtPrice.style.cssText = `text-decoration: line-through; color: #e32c2b; font-size: ${smallerSize}px; margin-right: 0.5em; font-weight: 400;`;
      compareAtPrice.innerHTML = '<span class="sr-only">Regulärer Preis</span>' + originalPrice;
      salePrice.before(compareAtPrice);

      // Update sale-price with NEW discounted price (GREEN, keep original size)
      salePrice.innerHTML = srOnlyHtml + discountedPrice;
      salePrice.classList.remove('text-subdued', 'text-on-sale');
      salePrice.style.color = '#307a07'; // Green (same as "Auf Lager")
      salePrice.style.fontWeight = '600';

      // Mark as processed
      priceList.dataset.voucherEnablerProcessed = 'true';
    });
  }

  /**
   * Revert all prices on the page to original (non-discounted) prices
   */
  function revertPricesOnPage() {
    // Revert Recharge widget prices
    revertRechargeWidgetPrices();

    const priceLists = document.querySelectorAll('price-list[data-voucher-enabler-processed]');

    priceLists.forEach(priceList => {
      // Skip cart items
      if (priceList.closest('cart-drawer, .cart, #cart, [data-cart], cart-items, line-item')) {
        return;
      }

      const salePrice = priceList.querySelector('sale-price');
      if (!salePrice) return;

      // Remove the voucher compare-at-price we added
      const voucherCompare = priceList.querySelector('compare-at-price.voucher-compare-price');
      if (voucherCompare) {
        voucherCompare.remove();
      }

      // Restore original innerHTML if we stored it
      if (priceList.dataset.voucherOriginalHtml) {
        salePrice.innerHTML = priceList.dataset.voucherOriginalHtml;

        // Restore original styling
        if (priceList.dataset.voucherOriginalStyle) {
          salePrice.setAttribute('style', priceList.dataset.voucherOriginalStyle);
        } else {
          salePrice.removeAttribute('style');
        }
        salePrice.className = priceList.dataset.voucherOriginalClasses || '';
      }

      // Clear processing markers
      delete priceList.dataset.voucherEnablerProcessed;
      delete priceList.dataset.voucherOriginalHtml;
      delete priceList.dataset.voucherOriginalStyle;
      delete priceList.dataset.voucherOriginalClasses;
    });
  }

  /**
   * Listen for cart drawer's "remove discount" button
   * When user removes discount via cart, mark as opted out
   */
  function initCartDiscountRemoveListener() {
    // Listen for clicks on remove discount buttons in cart
    document.addEventListener('click', function(e) {
      const target = e.target;
      // Check if clicked element is the remove discount button/link
      if (target.id === 'remove_discount' ||
          target.closest('#remove_discount') ||
          target.textContent.includes('Rabatt entfernen') ||
          target.closest('[data-remove-discount]')) {
        console.log('VoucherEnabler: User removed discount from cart');
        markVoucherRemoved();
        discountVerified = false;
        // Revert prices on page
        setTimeout(() => {
          revertPricesOnPage();
        }, 500);
      }
    });

    // Listen for cart:refresh events to re-verify discount status
    document.addEventListener('cart:refreshed', async function() {
      console.log('VoucherEnabler: Cart refreshed, re-verifying discount...');

      // If user has opted out, do nothing - let them use other discounts
      if (hasUserRemovedVoucher()) {
        return;
      }

      // Check if cart has products
      const cartEmpty = await isCartEmpty();

      if (cartEmpty) {
        // Cart is empty - keep current state based on cookie
        if (getCookie(COOKIE_NAME) === 'applied' && !discountVerified) {
          discountVerified = true;
          updatePricesOnPage();
        }
        return;
      }

      // Cart has products - verify discount is in cart
      // First check DOM (faster)
      const inDom = checkDiscountInDom();
      console.log('VoucherEnabler: Discount in DOM:', inDom);

      if (inDom) {
        if (!discountVerified) {
          discountVerified = true;
          markVoucherApplied();
          updatePricesOnPage();
        }
      } else {
        // Verify via server
        const verified = await verifyDiscountInCart();
        if (verified && !discountVerified) {
          discountVerified = true;
          markVoucherApplied();
          updatePricesOnPage();
        } else if (!verified && discountVerified) {
          // Discount was removed from cart
          console.log('VoucherEnabler: Discount no longer in cart');
          discountVerified = false;
          revertPricesOnPage();
        }
      }
    });
  }

  /**
   * Check if cart has any items
   */
  async function isCartEmpty() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js', {
        credentials: 'same-origin'
      });
      if (!response.ok) {
        console.log('VoucherEnabler: cart.js returned', response.status);
        return true;
      }
      const cart = await response.json();
      return !cart.items || cart.items.length === 0;
    } catch (error) {
      console.error('VoucherEnabler: Error checking cart:', error);
      return true; // Assume empty on error
    }
  }

  /**
   * Auto-apply voucher on first visit
   */
  async function autoApplyVoucher() {
    const cookieState = getCookie(COOKIE_NAME);
    console.log('VoucherEnabler: Cookie state:', cookieState);

    // If user has removed the voucher before, don't auto-apply
    if (hasUserRemovedVoucher()) {
      console.log('VoucherEnabler: User opted out, not applying');
      return;
    }

    // Check if cart has products
    const cartEmpty = await isCartEmpty();
    console.log('VoucherEnabler: Cart empty:', cartEmpty);

    // Check if we already applied the discount (cookie says applied)
    if (cookieState === 'applied') {
      console.log('VoucherEnabler: Cookie says applied');

      if (cartEmpty) {
        // Cart is empty - trust the cookie, discount is in session
        console.log('VoucherEnabler: Cart empty, trusting cookie');
        discountVerified = true;
        updatePricesOnPage();
        watchRechargeWidget();
        return;
      }

      // Cart has products - verify discount is actually in cart
      console.log('VoucherEnabler: Cart has products, verifying discount...');
      const isInCart = await verifyDiscountInCart();

      if (isInCart) {
        console.log('VoucherEnabler: Discount verified in cart');
        discountVerified = true;
        updatePricesOnPage();
        watchRechargeWidget();
      } else {
        console.log('VoucherEnabler: Discount NOT in cart, prices not updated');
        // Discount was removed or expired - clear cookie
        setCookie(COOKIE_NAME, '', -1);
        discountVerified = false;
      }
      return;
    }

    // First visit - apply discount to Shopify checkout session
    console.log('VoucherEnabler: First visit, applying discount code', DISCOUNT_CODE);
    const applied = await applyDiscount();

    if (!applied) {
      console.log('VoucherEnabler: Failed to apply discount');
      return;
    }

    // Discount endpoint succeeded
    if (cartEmpty) {
      // Cart is empty - trust the endpoint response
      console.log('VoucherEnabler: Cart empty, trusting endpoint response');
      discountVerified = true;
      markVoucherApplied();
      updatePricesOnPage();
      watchRechargeWidget();
    } else {
      // Cart has products - verify discount is in cart
      await new Promise(resolve => setTimeout(resolve, 300));
      const verified = await verifyDiscountInCart();

      if (verified) {
        console.log('VoucherEnabler: Discount verified in cart');
        discountVerified = true;
        markVoucherApplied();
        updatePricesOnPage();
        watchRechargeWidget();
      } else {
        console.log('VoucherEnabler: Discount NOT verified in cart');
        discountVerified = false;
      }
    }
  }

  /**
   * Watch for Recharge widget to load and update prices
   */
  function watchRechargeWidget() {
    if (!discountVerified) return;

    let retries = 0;
    const maxRetries = 20;

    const checkWidget = () => {
      // Only update if discount is still verified
      if (!discountVerified) return;

      const widgets = document.querySelectorAll('recharge-subscription-widget');
      let foundShadowRoot = false;

      widgets.forEach(widget => {
        if (widget.shadowRoot) {
          foundShadowRoot = true;
          const priceEl = widget.shadowRoot.querySelector('.rc-selection__onetime .rc-price:not([data-voucher-processed])');
          if (priceEl) {
            updateRechargeWidgetPrices();
          }
        }
      });

      // Keep retrying until we find the shadow root or max retries reached
      if (!foundShadowRoot && retries < maxRetries) {
        retries++;
        setTimeout(checkWidget, 500);
      }
    };

    // Start checking after a short delay
    setTimeout(checkWidget, 500);
  }

  /**
   * Initialize on DOM ready
   */
  function init() {
    console.log('VoucherEnabler: Initializing...', window.VOUCHER_ENABLER_CONFIG);

    // Check if config exists (plugin is enabled)
    if (!window.VOUCHER_ENABLER_CONFIG) {
      console.log('VoucherEnabler: No config found, exiting');
      return;
    }

    // Listen for cart discount removal
    initCartDiscountRemoveListener();

    // Auto-apply voucher on first visit (after short delay for page to settle)
    setTimeout(autoApplyVoucher, 500);

    // Watch for new content and update prices if discount is verified
    const observer = new MutationObserver(() => {
      if (discountVerified) {
        updatePricesOnPage();
        updateRechargeWidgetPrices();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Expose functions globally for debugging
  window.VoucherEnabler = {
    isActive: isVoucherActive,
    isVerified: () => discountVerified,
    hasOptedOut: hasUserRemovedVoucher,
    getCode: () => DISCOUNT_CODE,
    getPercent: () => DISCOUNT_PERCENT,
    verify: async () => {
      const result = await verifyDiscountInCart();
      console.log('VoucherEnabler: Manual verify result:', result);
      return result;
    },
    reset: () => {
      setCookie(COOKIE_NAME, '', -1);
      discountVerified = false;
      location.reload();
    }
  };

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
